/* ⛔ QUAND UN MODULE DONNE-T-IL DROIT A SON CERTIFICAT ? (06/10/2026)

   Regle de Nawal : un module TERMINE, c'est-a-dire `isDone && progress ===
   100`, la meme definition que « Ma certification » (MyCertif.js) et que
   l'espace client.

   Deux formes de module arrivent ici :
     - une ligne du plan (`actPlan[i]`, fenetre « Mon résultat ») : `progress`
       est un nombre fourni par le serveur ;
     - le module joue (`actFormation`, ecran de fin) : son `progress` est celui
       du chargement, et n'est recalcule qu'a l'envoi au serveur. On le
       recalcule donc ici comme la plateforme le fait (`calculateProgress`,
       UserContext.js) : sequences faites / sequences, arrondi.

   L'ICEBREAKER N'A PAS DE CERTIFICAT : ce n'est pas un module de formation
   (decision de Nawal du 06/10/2026). `actPlan[0]` est l'icebreaker, lecture
   deja faite par App.js et Dashboard.js ; un module de type « icebreaker »
   (champ `type` du contenu, choisi dans l'editeur) l'est aussi.

   ⛔ L'ESSAI DEPUIS L'ADMIN (06/10/2026, demande de Nawal : voir le
   certificat quand elle finit un module en essai). Deux differences :
     - le module est TERMINE des que toutes ses sequences sont faites : l'essai
       repart d'une ardoise vierge et n'enregistre rien, on n'exige donc pas
       `isDone`, que seul le serveur garantit ;
     - le plan de l'essai ne contient QUE le module essaye (FormationPlayer,
       `planSimule`) : `actPlan[0]` y est le module lui-meme, et la regle
       ci-dessus le prenait pour l'icebreaker. En essai, seul le type compte.

   ⛔ LE SCORE DU MODULE DOIT ATTEINDRE 50 % (regle de Nawal du 07/10/2026,
   qui remplace « plus de 50 % » du 06/10 : un compte de demo a 50 pile
   n'avait pas de certificat). 50 suffit. Le seuil vit dans
   `SEUIL_SCORE_CERTIFICAT`, et nulle part ailleurs.
   ⛔ (07/10/2026, fiche « Systeme de scoring » de Nawal, A.4) Le CERTIFICAT
   ne lit que le score du MODULE, `resultFin.glbMaster.score` : la moyenne
   des categories de connaissance que Nawal coche « oui ». Pas de score de
   module (rien de coche) : pas de certificat, JAMAIS de repli sur
   `master`, qui ferait valider des competences
   (scoreSuffisantPourCertificat). L'AFFICHAGE, lui, garde sa lecture :
   le score global que la plateforme affiche deja (scoreGlobalDuModule) :
   `resultFin.glbMaster.score` s'il existe, sinon `resultFin.master.score`,
   la meme lecture que la jauge de « Mon résultat » (Dashboard.js,
   `resultModalValue`) et que `finalScore` a l'ecran de fin
   (FinDeFormation.js), essai compris. Il est ARRONDI comme la jauge l'affiche
   (CircularGauge.jsx, `Math.round`) : un 49,5 s'affiche 50 et donne le
   certificat, un 49,4 s'affiche 49 et ne le donne pas ; ce que l'apprenant
   lit sur la jauge decide.
   Pas de resultFin, ou pas de score dedans : pas de certificat.
   ⚠️ On LIT le score, on ne le calcule pas : le calcul reste celui de
   `getFormationResults` (UserContext.js), point 3.3 d'Hicham. */

export const SEUIL_SCORE_CERTIFICAT = 50

/* score global affiche (arrondi), ou null s'il n'y en a pas */
export const scoreGlobalDuModule = (module) => {
  let resultFin = module?.resultFin
  if (typeof resultFin === 'string') {
    try { resultFin = JSON.parse(resultFin) } catch (e) { return null }
  }
  if (!resultFin || typeof resultFin !== 'object') return null
  const lire = (cat) => {
    const s = resultFin?.[cat]?.score
    if (s === null || s === undefined || s === '' || typeof s === 'boolean') return null
    const n = Number(s)
    return Number.isFinite(n) ? n : null
  }
  /* meme ordre que l'affichage : glbMaster s'il a un score, sinon master */
  const brut = resultFin?.glbMaster?.score !== undefined && resultFin?.glbMaster?.score !== null
    ? lire('glbMaster')
    : lire('master')
  return brut === null ? null : Math.round(brut)
}

/* score du module seul (glbMaster), arrondi comme la jauge, ou null : c'est
   lui qui decide du certificat (fiche scoring du 07/10, A.4) */
export const scoreDuModulePourCertificat = (module) => {
  let resultFin = module?.resultFin
  if (typeof resultFin === 'string') {
    try { resultFin = JSON.parse(resultFin) } catch (e) { return null }
  }
  const s = resultFin?.glbMaster?.score
  if (s === null || s === undefined || s === '' || typeof s === 'boolean') return null
  const n = Number(s)
  return Number.isFinite(n) ? Math.round(n) : null
}

export const scoreSuffisantPourCertificat = (module) => {
  const score = scoreDuModulePourCertificat(module)
  return score !== null && score >= SEUIL_SCORE_CERTIFICAT
}

export const progressionDuModule = (module) => {
  if (Array.isArray(module?.chapitre)) {
    let total = 0, faites = 0
    module.chapitre.forEach((ch) => (ch?.partie || []).forEach((pa) => (pa?.sequence || []).forEach((se) => {
      total++
      if (se?.isDone) faites++
    })))
    if (total > 0) return Math.round((faites / total) * 100)
  }
  const p = module?.progress
  if (p === null || p === undefined || p === false || p === '') return null
  const n = Number(p)
  return Number.isFinite(n) ? n : null
}

/* exclu du certificat : decision de Nawal du 06/10/2026 */
export const estIcebreaker = (module, actPlan, { essai = false } = {}) => {
  if (module?.type === 'icebreaker') return true
  if (essai) return false
  const id = actPlan?.[0]?.id
  return id !== null && id !== undefined && module?.id === id
}

export const moduleTermine = (module, { essai = false } = {}) =>
  (essai || module?.isDone === true) && progressionDuModule(module) === 100

export const certificatDisponible = ({ module, actPlan, prenomNom, essai = false }) =>
  moduleTermine(module, { essai })
  && !estIcebreaker(module, actPlan, { essai })
  && scoreSuffisantPourCertificat(module)
  && !!String(module?.name || '').trim()
  && !!String(prenomNom || '').trim()
